import React, { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { Outlet, useParams } from "react-router";
import { useAuth, useLinks, useMissionRole } from "hooks";
import { ROLE_STATES_LABELS, STATUS_COLORS } from "constants/index";
import PageTitle, { LAYOUT } from "components/PageTitle";
import { PrimaryLink } from "components/Links";
import { NavList } from "components/Nav";
import { BannerShowcaseSignup } from "components/Banner";
import { ApplyButton, RoleAdvancedOptions } from "components/Missions/Roles";
import { Tag } from "components/Tags";
import { formatDate } from "utils";
import { colors, fontSize, margins } from "style";

/**
 * MissionRole
 */
const MissionRole = () => {
  const {
    isAdmin,
    isClient,
    isShowcase,
    isTalentSimpleClient,
    canManageRoleAttachments,
    canApplyForRolesOnBehalf,
    canViewRolePublishedDate,
  } = useAuth();
  const links = useLinks();
  const [navItems, setNavItems] = useState([]);
  const { id, roleId } = useParams();
  const {
    loading,
    data: selectedRole,
    error,
    refetch,
  } = useMissionRole({
    id: roleId,
    includeCustomerBudget: isAdmin || isTalentSimpleClient,
    includeDisabledChallenges: isAdmin,
    includeAttachments: canManageRoleAttachments,
  });
  const mission = selectedRole?.mission;

  const MissionBoardBackLink = () => {
    if (isClient || isShowcase) {
      return links.client_mission_board;
    } else {
      return links.mission(mission?.uuid);
    }
  };

  useEffect(() => {
    setNavItems(
      pages(id, roleId, isAdmin, isClient, isShowcase, isTalentSimpleClient, links).filter((page) => page.isPermissible)
    );
  }, [id, roleId, isAdmin, isTalentSimpleClient]);

  return (
    <>
      {(loading || selectedRole) && (
        <>
          <PageTitle
            backLink={
              <PrimaryLink to={MissionBoardBackLink()} routerEnabled>
                {isClient || isShowcase ? <FormattedMessage id="Missions.Mission.Role.Title" /> : mission?.name}
              </PrimaryLink>
            }
            loading={loading}
            title={selectedRole?.name}
            subtitle={
              <div
                css={css`
                  padding-top: 2rem;
                `}
              >
                <NavList pages={navItems} />
              </div>
            }
            layout={LAYOUT.horizontal}
            actionBar={
              <StatusWrapper noActions={!isAdmin}>
                {!loading && canViewRolePublishedDate && (
                  <RightContainer>
                    <Tag
                      // FIXME: Swap colors to correct props in Tag component
                      background={STATUS_COLORS[selectedRole?.state]?.color}
                      color={STATUS_COLORS[selectedRole?.state]?.background}
                    >
                      {ROLE_STATES_LABELS[selectedRole?.state]}
                    </Tag>
                    <PublishedAt>
                      {selectedRole?.publishedAt && formatDate(selectedRole?.publishedAt, "MMM DD, YYYY", true, true)}
                    </PublishedAt>
                  </RightContainer>
                )}
                <ApplyButton role={selectedRole} canApplyOnBehalf={canApplyForRolesOnBehalf} roleLoading={loading}>
                  <FormattedMessage id={`Missions.Apply${canApplyForRolesOnBehalf ? ".OnBehalf" : ""}`} />
                </ApplyButton>
                <RoleAdvancedOptions roleId={selectedRole?.id} data={selectedRole} loading={loading} />
              </StatusWrapper>
            }
            asHeader
          >
            <BannerShowcaseSignup
              css={css`
                padding: 4rem 0 0 0;
              `}
            />
          </PageTitle>
        </>
      )}
      <Outlet
        context={{
          selectedRole,
          loading,
          error,
          refetch,
          mission,
        }}
      />
    </>
  );
};

const defaultPageProps = {
  routerEnabled: true,
  end: false,
  isActive: true,
};

const pages = (missionId, roleId, isAdmin, isClient, isShowcase, isTalentSimpleClient, links) => [
  {
    label: (
      <FormattedMessage id={`Missions.MissionRole.${isTalentSimpleClient ? "Overview" : "Tabs.Details"}`} key="tab1" />
    ),
    to: links.mission_role_details(missionId, roleId),
    isPermissible: isAdmin || isClient,
    props: defaultPageProps,
  },
  {
    label: (
      <FormattedMessage id={`Missions.MissionRole.Tabs.${isTalentSimpleClient ? "Blueprint" : "Vetting"}`} key="tab6" />
    ),
    to: links.mission_role_vetting(missionId, roleId),
    isPermissible: isClient,
    props: defaultPageProps,
  },
  {
    label: <FormattedMessage id="Missions.MissionRole.Tabs.Recommendations" key="tab2" />,
    to: links.mission_role_recommendations(missionId, roleId),
    isPermissible: isAdmin,
    props: defaultPageProps,
  },
  {
    label: <FormattedMessage id="Missions.MissionRole.Tabs.Applications" key="tab3" />,
    to: links.mission_role_applications(missionId, roleId),
    isPermissible: isAdmin || isClient || isShowcase,
    props: defaultPageProps,
  },
  {
    label: <FormattedMessage id="Missions.MissionRole.Tabs.Discussions" key="tab4" />,
    to: links.mission_role_discussions(missionId, roleId),
    isPermissible: isAdmin || isClient,
    props: defaultPageProps,
  },
  {
    label: <FormattedMessage id="Missions.MissionRole.Tabs.Timeline" key="tab5" />,
    to: links.mission_role_timeline(missionId, roleId),
    isPermissible: isAdmin,
    props: defaultPageProps,
    isAdmin: true,
  },
];

const PublishedAt = styled.div`
  font-size: ${fontSize.xsmall};
  color: ${colors.grayAnatomyBase};
  font-weight: 500;
`;

const StatusWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: end;
  gap: ${margins.xxsmall};
  ${({ noActions }) => noActions && `margin-bottom: 5rem;`}
`;

const RightContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: end;
  gap: ${margins.xxsmall};
`;

export default MissionRole;
